// Cost calculation utility based on actual ingredient pricing data
// Data derived from detailed cost analysis spreadsheet

export const INGREDIENT_COSTS = {
  // Cost per burrito in dollars
  cheese: 0.0331666667,
  blackBeans: 0.171125,
  guacamole: 0.034625,
  salsa: 0.0218125,
  sourCream: 0.0615,
  organicBrownRice: 0.0312083333,
  jalapenos: 0.0230625,
  tacoSeasoning: 0.0166633333,
  corn: 0.0235208333,
  bellPeppers: 0.2165,
  romaineLettuce: 0.5406041667,
  jumboTortillas: 0.1763333333
};

export const FIXED_EVENT_COSTS = {
  // Fixed costs per event regardless of burrito count
  gloves: 12.99,
  tablecloths: 3.70266667,
  foilSheets: 13.69
};

// Cost per burrito (sum of all ingredient costs)
export const COST_PER_BURRITO = Object.values(INGREDIENT_COSTS).reduce((sum, cost) => sum + cost, 0);

// Total fixed costs per event
export const TOTAL_FIXED_COSTS = Object.values(FIXED_EVENT_COSTS).reduce((sum, cost) => sum + cost, 0);

/**
 * Calculate total cost for a given number of burritos
 * @param {number} burritoCount - Number of burritos to make
 * @returns {number} Total cost in dollars
 */
export const calculateTotalCost = (burritoCount) => {
  return (burritoCount * COST_PER_BURRITO) + TOTAL_FIXED_COSTS;
};

/**
 * Calculate cost breakdown for a given number of burritos
 * @param {number} burritoCount - Number of burritos to make
 * @returns {object} Detailed cost breakdown
 */
export const calculateCostBreakdown = (burritoCount) => {
  const ingredientTotal = burritoCount * COST_PER_BURRITO;
  const fixedTotal = TOTAL_FIXED_COSTS;
  const grandTotal = ingredientTotal + fixedTotal;

  return {
    burritoCount,
    costPerBurrito: COST_PER_BURRITO,
    ingredientCosts: {
      total: ingredientTotal,
      perBurrito: COST_PER_BURRITO,
      breakdown: Object.entries(INGREDIENT_COSTS).map(([ingredient, cost]) => ({
        ingredient,
        costPerBurrito: cost,
        totalCost: cost * burritoCount
      }))
    },
    fixedCosts: {
      total: fixedTotal,
      breakdown: Object.entries(FIXED_EVENT_COSTS).map(([item, cost]) => ({
        item,
        cost
      }))
    },
    grandTotal
  };
};

/**
 * Round cost to nearest $25 or $50 increment
 * @param {number} cost - Raw cost to round
 * @returns {number} Rounded cost
 */
export const roundToNearestDonationAmount = (cost) => {
  // Round to nearest $25 for costs under $500, $50 for costs over $500
  const increment = cost < 500 ? 25 : 50;
  return Math.ceil(cost / increment) * increment;
};

/**
 * Get suggested donation amount for a given number of burritos
 * @param {number} burritoCount - Number of burritos to make
 * @returns {number} Suggested donation amount (rounded)
 */
export const getSuggestedDonationAmount = (burritoCount) => {
  const totalCost = calculateTotalCost(burritoCount);
  return roundToNearestDonationAmount(totalCost);
};

/**
 * Get common burrito counts and their donation amounts
 * @returns {array} Array of common burrito counts with suggested donations
 */
export const getCommonDonationAmounts = () => {
  const commonCounts = [50, 100, 150, 200, 210, 250, 300, 400, 500];
  
  return commonCounts.map(count => ({
    burritoCount: count,
    actualCost: calculateTotalCost(count),
    suggestedDonation: getSuggestedDonationAmount(count),
    description: `${count} Burritos Event`
  }));
};

/**
 * Calculate how many burritos can be made with a given donation amount
 * @param {number} donationAmount - Available donation amount
 * @returns {object} Burrito count and remaining funds
 */
export const calculateBurritosFromDonation = (donationAmount) => {
  const availableForBurritos = donationAmount - TOTAL_FIXED_COSTS;
  
  if (availableForBurritos <= 0) {
    return {
      burritoCount: 0,
      totalCost: TOTAL_FIXED_COSTS,
      remainingFunds: donationAmount - TOTAL_FIXED_COSTS
    };
  }

  const burritoCount = Math.floor(availableForBurritos / COST_PER_BURRITO);
  const totalCost = calculateTotalCost(burritoCount);
  const remainingFunds = donationAmount - totalCost;

  return {
    burritoCount,
    totalCost,
    remainingFunds
  };
}; 